<template>
    <!-- Per-member Affiliate eligibility (owner 2026-10-16): only members granted
         here see the Affiliate menu on www / LINE and may use the program.
         Used on the BO member page (tab "Affiliate") and in the affiliate drawer. -->
    <div v-loading="loading" class="aff-elig" data-testid="aff-elig-panel">
        <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon :title="$t('marketing.affiliate.eligibility.schema_missing')" data-testid="aff-elig-schema"></el-alert>
        <template v-else-if="d">
            <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
                <span class="font-weight-bold">{{ $t("marketing.affiliate.eligibility.title") }}</span>
                <el-tag size="small" :type="tag" data-testid="aff-elig-status">{{ tk("marketing.affiliate.eligibility.status_", key) }}</el-tag>
                <el-tag v-if="d.affiliate" size="mini" type="info">{{ $t("marketing.affiliate.eligibility.joined_as", { code: d.affiliate.code }) }}</el-tag>
            </div>
            <p class="small text-muted mb-2">{{ $t("marketing.affiliate.eligibility.hint") }}</p>
            <el-alert v-if="!d.program_enabled" type="warning" :closable="false" show-icon class="mb-2" :title="$t('marketing.affiliate.eligibility.program_off')"></el-alert>
            <p v-if="d.granted_at" class="small mb-1">
                {{ $t("marketing.affiliate.eligibility.granted_by", { name: d.granted_by_name || "-", at: fmt(d.granted_at) }) }}
            </p>
            <p v-if="d.revoked_at" class="small mb-1">
                {{ $t("marketing.affiliate.eligibility.revoked_by", { name: d.revoked_by_name || "-", at: fmt(d.revoked_at) }) }}
            </p>
            <p v-if="d.note" class="small mb-2">{{ $t("marketing.affiliate.eligibility.note") }}: {{ d.note }}</p>

            <div v-if="canWrite && !d.member_deleted" class="d-flex flex-wrap align-items-start mt-2" style="gap: 8px;">
                <el-input v-model="note" size="small" :maxlength="noteMax" show-word-limit :placeholder="$t('marketing.affiliate.eligibility.note_placeholder')" style="max-width: 360px;" data-testid="aff-elig-note"></el-input>
                <base-button v-if="key !== 'eligible'" type="success" size="sm" :loading="acting" data-testid="aff-elig-grant" @click="act(true)">
                    <i class="fa fa-check"></i> {{ $t("marketing.affiliate.eligibility.grant") }}
                </base-button>
                <base-button v-else type="danger" size="sm" :loading="acting" data-testid="aff-elig-revoke" @click="act(false)">
                    <i class="fa fa-ban"></i> {{ $t("marketing.affiliate.eligibility.revoke") }}
                </base-button>
            </div>

            <div v-if="d.audit && d.audit.length" class="mt-3">
                <div class="small text-muted text-uppercase mb-1">{{ $t("marketing.affiliate.eligibility.history") }}</div>
                <el-table :data="d.audit" size="small" class="aff-table bon-table" data-testid="aff-elig-audit">
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.eligibility.when')" width="150"><template v-slot="{ row }">{{ fmt(row.created_at) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.eligibility.action')" width="110">
                        <template v-slot="{ row }">{{ tk("marketing.affiliate.eligibility.audit_", row.action === "eligibility_grant" ? "grant" : "revoke") }}</template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.eligibility.by')" width="140" prop="actor_name"></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.eligibility.note')" min-width="160">
                        <template v-slot="{ row }">{{ (row.detail && row.detail.note) || "-" }}</template>
                    </el-table-column>
                </el-table>
            </div>
        </template>
    </div>
</template>

<script>
import { Tag, Alert, Input, Table, TableColumn, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliateService } from "@/resource";
import { isSchemaMissing } from "@/utils/marketing";
import { ELIGIBILITY_NOTE_MAX, eligibilityKey, eligibilityTag, eligibilityNoteError } from "@/utils/affiliate";
import affiliateMixin from "./affiliateMixin";

export default {
    name: "AffiliateEligibilityPanel",
    directives: { loading: Loading.directive },
    components: {
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [Input.name]: Input,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    },
    mixins: [affiliateMixin],
    props: {
        memberId: { type: [Number, String], default: null }
    },
    data() {
        return { loading: false, acting: false, schemaMissing: false, d: null, note: "", noteMax: ELIGIBILITY_NOTE_MAX };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" }),
        key() {
            return eligibilityKey(this.d);
        },
        tag() {
            return eligibilityTag(this.key);
        }
    },
    watch: {
        memberId: {
            immediate: true,
            handler(v) {
                if (v) this.load();
            }
        }
    },
    methods: {
        async load() {
            if (!this.memberId) return;
            this.loading = true;
            try {
                this.d = this.unwrap(await AffiliateService.eligibility(this.memberId));
                this.schemaMissing = false;
            } catch (err) {
                this.d = null;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
            }
        },
        async act(grant) {
            const bad = eligibilityNoteError(this.note);
            if (bad) {
                this.$notify({ type: "danger", message: this.$t("marketing.affiliate.eligibility." + bad) });
                return;
            }
            const text = this.$t(grant ? "marketing.affiliate.eligibility.confirm_grant" : "marketing.affiliate.eligibility.confirm_revoke");
            if (!(await this.confirmBox(text, { danger: !grant, icon: grant ? "question" : "warning" }))) return;
            this.acting = true;
            try {
                const payload = { note: String(this.note || "").trim() };
                const res = grant ? await AffiliateService.grantEligibility(this.memberId, payload) : await AffiliateService.revokeEligibility(this.memberId, payload);
                this.d = this.unwrap(res);
                this.note = "";
                this.notifyOk(grant ? "marketing.affiliate.eligibility.granted" : "marketing.affiliate.eligibility.revoked");
                this.$emit("changed", this.d);
            } catch (err) {
                this.showError(err);
            } finally {
                this.acting = false;
            }
        }
    }
};
</script>
